import { useEffect, useState } from 'react';
import { ApiError, type PharmacyApprovalRow, type PharmacyDocumentSummary } from '@chicare/shared';

import {
  useDecideLicenceRenewal,
  useDecidePharmacy,
  useLicenceRenewals,
  usePharmacyApproval,
  usePharmacyApprovals,
  useReviewDocument,
} from '@/lib/onboardingQueries';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from './AdminError';

const TABS = [
  ['pending_approval', 'Waiting'],
  ['active', 'Live'],
  ['rejected', 'Rejected'],
  ['suspended', 'Paused'],
  ['all', 'All'],
] as const;

/**
 * The licence gate.
 *
 * ── THIS SCREEN IS WHY BUYERS TRUST CHICARE ─────────────────────────────────
 *
 * The trust banner, the authenticity promise, every reason somebody chooses
 * ChiCare over a stranger on WhatsApp — all of it rests on a person here having
 * actually opened a PCN licence and matched it to a real premises.
 *
 * So the approve button is disabled until every document has been individually
 * opened and marked, and the screen says WHY it is disabled rather than just
 * looking broken. A reviewer who cannot tell the difference between "not ready"
 * and "not working" will eventually start clicking through.
 */
export function PharmacyApprovalScreen() {
  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      {/* Renewals sit ABOVE the registration queue, always. A pharmacy waiting
          on a first approval has never sold anything; one waiting on a renewal
          is dark right now and losing orders by the hour. Same work, and the
          order it is worked in is the whole difference to that seller. */}
      <RenewalQueue />
      <PharmacyQueue />
    </div>
  );
}

/**
 * Licences sent in by pharmacies that are already trading.
 *
 * Renders nothing when the queue is empty — an empty panel above every other
 * screen is how people learn to scroll past the place the urgent thing appears.
 */
function RenewalQueue() {
  const { data } = useLicenceRenewals('pending');
  const decide = useDecideLicenceRenewal();
  const [note, setNote] = useState<Record<number, string>>({});

  const renewals = data?.renewals ?? [];

  if (renewals.length === 0) {
    return null;
  }

  // Dark shops first, then longest waiting. Nothing else is a useful ordering.
  const ordered = [...renewals].sort(
    (a, b) => Number(b.currently_dark) - Number(a.currently_dark) || b.waiting_days - a.waiting_days,
  );

  return (
    <section className="mb-7" data-testid="renewal-queue">
      <h2 className="m-0 font-display text-[18px] font-bold tracking-[-0.02em]">
        Licence renewals ({renewals.length})
      </h2>
      <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
        These are pharmacies already on ChiCare sending in a current licence. Open the scan, check
        the number and the date against the register, then approve — the listings come back on
        their own. Anyone marked paused has nothing on sale until you do.
      </p>

      <div className="mt-3 space-y-2.5">
        {ordered.map((renewal) => (
          <div
            key={renewal.id}
            className={cn(
              'rounded-card border bg-white p-4',
              renewal.currently_dark ? 'border-danger' : 'border-line',
            )}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
                  {renewal.pharmacy}
                  {renewal.currently_dark ? <Badge tone="danger">Paused — nothing on sale</Badge> : null}
                  {renewal.sector === 'veterinary' ? <Badge tone="neutral">Animal health</Badge> : null}
                </p>
                <p className="m-0 mt-0.5 text-[12.5px] text-muted">
                  {renewal.label} · {renewal.licence_no} · expires {renewal.expiry_date}
                </p>
                <p className="m-0 mt-0.5 text-[11.5px] text-faint">
                  Waiting {renewal.waiting_days} day{renewal.waiting_days === 1 ? '' : 's'}
                </p>
              </div>

              <a
                href={`/api/v1/admin/pharmacies/${renewal.pharmacy_id}/renewals/${renewal.id}/document`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 items-center rounded-btn border border-line px-3.5 text-[13px] font-semibold text-ink no-underline"
              >
                Open the scan
              </a>
            </div>

            <textarea
              value={note[renewal.id] ?? ''}
              onChange={(event) => setNote((current) => ({ ...current, [renewal.id]: event.target.value }))}
              rows={2}
              placeholder="What did you check it against? In six months this is the only record."
              className="mt-3 w-full rounded-btn border border-line bg-paper p-2.5 text-[13px] text-ink outline-none focus:border-blue"
            />

            <div className="mt-2.5 flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={decide.isPending || (note[renewal.id] ?? '').trim().length < 5}
                onClick={() =>
                  decide.mutate({
                    action: 'approve',
                    pharmacyId: renewal.pharmacy_id,
                    renewalId: renewal.id,
                    reason: note[renewal.id] ?? '',
                  })
                }
              >
                Approve and restore
              </Button>
              <Button
                size="sm"
                variant="grey"
                disabled={decide.isPending || (note[renewal.id] ?? '').trim().length < 10}
                onClick={() =>
                  decide.mutate({
                    action: 'reject',
                    pharmacyId: renewal.pharmacy_id,
                    renewalId: renewal.id,
                    note: note[renewal.id] ?? '',
                  })
                }
              >
                Reject
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PharmacyQueue() {
  const [status, setStatus] = useState<string>('pending_approval');
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // Debounced so typing a licence number does not fire eight searches.
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error, refetch } = usePharmacyApprovals(status, query);

  if (isError) {
    return (
      <AdminError title="We couldn't load the approval queue" error={error} onRetry={() => refetch()} />
    );
  }

  return (
    <div>
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Pharmacies</h1>
        <p className="mt-1 max-w-[70ch] text-[13px] leading-relaxed text-muted">
          Every pharmacy on ChiCare is checked by hand before it can sell. Open each document,
          match the licence to the premises, then approve — the note you write is the only record
          of what you checked.
        </p>
      </header>

      <div className="mb-3">
        <label className="relative block">
          <span className="sr-only">Search pharmacies</span>
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
          />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setSelected(null);
            }}
            placeholder="Name, PCN licence number, RC number, LGA, phone or email"
            className="min-h-12 w-full rounded-btn border border-line bg-white pl-9 pr-3 text-[13.5px] text-ink outline-none focus:border-blue"
          />
        </label>
        {query ? (
          <p className="m-0 mt-1.5 text-[12px] text-muted">
            {data?.matched ?? 0} match{(data?.matched ?? 0) === 1 ? '' : 'es'} for
            <b className="text-ink"> {query}</b>
            {status !== 'all' ? (
              <>
                {' '}
                in this tab ·{' '}
                <button
                  type="button"
                  onClick={() => setStatus('all')}
                  className="bg-transparent p-0 font-medium text-blue underline"
                >
                  search every status
                </button>
              </>
            ) : null}
          </p>
        ) : null}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setStatus(key);
              setSelected(null);
            }}
            className={cn(
              'flex min-h-11 items-center gap-2 rounded-btn border px-3.5 text-[13px] font-medium',
              status === key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {label}
            {data?.counts?.[key] ? (
              <span
                className={cn(
                  'rounded-full px-1.5 font-mono text-[11px] font-bold',
                  status === key ? 'bg-white/25 text-white' : 'bg-paper text-ink',
                )}
              >
                {data.counts[key]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" radius="card" />
          <Skeleton className="h-24 w-full" radius="card" />
        </div>
      ) : data && data.pharmacies.length === 0 ? (
        <EmptyState
          title={query ? 'Nothing matched' : status === 'pending_approval' ? 'Nothing waiting' : 'Nothing here'}
          body={
            query
              ? `No pharmacy matches "${query}"${status === 'all' ? '' : ' in this tab'}.`
              : status === 'pending_approval'
                ? 'No pharmacy is waiting to be reviewed.'
                : 'No pharmacies with this status.'
          }
          action={
            query ? (
              <Button variant="ghost" onClick={() => setSearch('')}>
                Clear the search
              </Button>
            ) : status === 'all' ? (
              <Button variant="ghost" icon="refresh" onClick={() => refetch()}>
                Check again
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setStatus('all')}>
                See every pharmacy
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-3">
          {data?.pharmacies.map((pharmacy) => (
            <PharmacyCard
              key={pharmacy.id}
              pharmacy={pharmacy}
              open={selected === pharmacy.id}
              onToggle={() => setSelected(selected === pharmacy.id ? null : pharmacy.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PharmacyCard({
  pharmacy,
  open,
  onToggle,
}: {
  pharmacy: PharmacyApprovalRow;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className={cn('overflow-hidden p-0', open && 'ring-1 ring-blue')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-col gap-3 p-4 text-left sm:flex-row sm:items-center"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-bold text-ink">{pharmacy.name}</span>
            {pharmacy.licence_expired ? <Badge tone="danger">Licence expired</Badge> : null}
          </div>
          <p className="m-0 mt-1 text-[12.5px] text-muted">
            {[pharmacy.lga, pharmacy.state].filter(Boolean).join(', ') || 'No location given'}
            {pharmacy.status === 'pending_approval'
              ? ` · waiting ${pharmacy.waiting_days} day${pharmacy.waiting_days === 1 ? '' : 's'}`
              : null}
          </p>
        </div>

        <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-1">
          <span
            className={cn(
              'font-mono text-[12px] font-bold tabular-nums',
              pharmacy.documents_approved === pharmacy.documents_required
                ? 'text-verified'
                : 'text-muted',
            )}
          >
            {pharmacy.documents_approved}/{pharmacy.documents_required} checked
          </span>
          <span className="text-[11.5px] text-faint">{pharmacy.documents_uploaded} uploaded</span>
        </div>
      </button>

      {open ? <ApprovalDetail id={pharmacy.id} /> : null}
    </Card>
  );
}

function ApprovalDetail({ id }: { id: number }) {
  const { data, isLoading, isError, error, refetch } = usePharmacyApproval(id);

  if (isError) {
    return (
      <div className="border-t border-line p-4">
        <AdminError title="We couldn't load this pharmacy" error={error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-3 border-t border-line p-4">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const { pharmacy, meta } = data;

  return (
    <div className="border-t border-line bg-paper p-4">
      <section className="mb-4">
        <h3 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
          What they told us
        </h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-[12.5px] sm:grid-cols-2">
          <Fact label="Premises" value={pharmacy.premises_address} />
          <Fact label="PCN licence" value={pharmacy.pcn_licence_no} mono />
          <Fact
            label="Expires"
            value={pharmacy.pcn_licence_expiry}
            tone={pharmacy.licence_expired ? 'danger' : undefined}
          />
          <Fact label="Superintendent" value={pharmacy.superintendent_name} />
          <Fact label="RC number" value={pharmacy.rc_number} mono />
          <Fact
            label="Bank details"
            value={pharmacy.has_bank_details ? `${pharmacy.account_name} · ${pharmacy.bank_code}` : null}
            tone={pharmacy.has_bank_details ? undefined : 'danger'}
          />
          <Fact label="Contact" value={pharmacy.owner.name} />
          <Fact label="Phone" value={pharmacy.owner.phone} mono />
        </dl>
      </section>

      <section className="mb-4">
        <h3 className="m-0 mb-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
          Documents — open each one
        </h3>
        <div className="space-y-2.5">
          {pharmacy.documents.map((doc) => (
            <DocumentReviewRow key={doc.id} pharmacyId={pharmacy.id} document={doc} />
          ))}
          {pharmacy.documents.length === 0 ? (
            <p className="m-0 text-[13px] text-muted">Nothing uploaded yet.</p>
          ) : null}
        </div>
      </section>

      <DecisionPanel id={pharmacy.id} blockers={meta.blockers} canApprove={meta.can_approve} />
    </div>
  );
}

/**
 * One document: open it, then say whether it is good.
 *
 * Fetched as a blob with the bearer token rather than linked directly — these
 * are private files on a private disk, and a licence carries a superintendent's
 * name and registration number.
 */
function DocumentReviewRow({
  pharmacyId,
  document: doc,
}: {
  pharmacyId: number;
  document: PharmacyDocumentSummary;
}) {
  const review = useReviewDocument();
  const [url, setUrl] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [openError, setOpenError] = useState<string | null>(null);

  // Object URLs leak memory if they are never released.
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const open = async () => {
    setOpenError(null);

    try {
      const blob = await api.pharmacyDocumentBlob(pharmacyId, doc.id as number);
      const objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      window.open(objectUrl, '_blank', 'noopener');
    } catch (error) {
      setOpenError(error instanceof Error ? error.message : 'That document could not be opened.');
    }
  };

  return (
    <div
      className={cn(
        'rounded-card border p-3',
        doc.status === 'approved'
          ? 'border-verified bg-verified-tint'
          : doc.status === 'rejected'
            ? 'border-danger bg-danger-tint'
            : 'border-line bg-white',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 text-[13.5px] font-semibold text-ink">{doc.label}</p>
          <p className="m-0 mt-0.5 text-[12px] text-muted">
            {doc.status === 'approved'
              ? 'Approved'
              : doc.status === 'rejected'
                ? 'Rejected'
                : 'Not checked yet'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="grey" icon="eye" onClick={open}>
            Open
          </Button>

          {doc.status !== 'approved' ? (
            <Button
              size="sm"
              variant="ok"
              disabled={review.isPending}
              onClick={() =>
                review.mutate({ pharmacyId, documentId: doc.id as number, status: 'approved' })
              }
            >
              Approve
            </Button>
          ) : null}

          {doc.status !== 'rejected' ? (
            <Button size="sm" variant="bad" onClick={() => setRejecting(!rejecting)}>
              Reject
            </Button>
          ) : null}
        </div>
      </div>

      {openError ? (
        <p role="alert" className="m-0 mt-2 text-[12.5px] font-medium text-danger">
          {openError}
        </p>
      ) : null}

      {doc.note ? (
        <p className="m-0 mt-2 text-[12.5px] leading-relaxed text-muted">{doc.note}</p>
      ) : null}

      {rejecting ? (
        <div className="mt-2.5">
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            placeholder="The scan is cut off — we cannot read the expiry date."
            className="w-full rounded-btn border border-line bg-white p-2.5 text-[13px] text-ink outline-none focus:border-blue"
          />
          <p className="m-0 mb-2 mt-1 text-[11.5px] text-muted">
            The pharmacy reads this and has to be able to fix it.
          </p>
          <Button
            size="sm"
            variant="bad"
            disabled={note.trim().length < 5 || review.isPending}
            onClick={() =>
              review.mutate(
                { pharmacyId, documentId: doc.id as number, status: 'rejected', note },
                { onSuccess: () => setRejecting(false) },
              )
            }
          >
            Send this back
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Approve or reject the pharmacy itself.
 *
 * When approval is blocked the reasons are listed, because a disabled button
 * with no explanation is indistinguishable from a broken one.
 */
function DecisionPanel({
  id,
  blockers,
  canApprove,
}: {
  id: number;
  blockers: string[];
  canApprove: boolean;
}) {
  const decide = useDecidePharmacy();
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'approve' | 'reject'>('approve');

  return (
    <section className="rounded-card border border-line bg-white p-3.5">
      {blockers.length > 0 ? (
        <div data-testid="approval-blockers" className="mb-3">
          <p className="m-0 mb-1.5 text-[12.5px] font-bold text-ink">
            Cannot approve yet — {blockers.length} thing{blockers.length === 1 ? '' : 's'} outstanding
          </p>
          <ul className="m-0 list-none space-y-1 p-0">
            {blockers.map((blocker) => (
              <li key={blocker} className="flex items-start gap-1.5 text-[12.5px] text-muted">
                <Icon name="x" size={13} className="mt-0.5 shrink-0 text-danger" />
                {blocker}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mb-2.5 flex gap-2">
        {(['approve', 'reject'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            className={cn(
              'min-h-11 flex-1 rounded-btn border px-3 text-[13px] font-medium capitalize',
              mode === option ? 'border-blue bg-blue-tint text-blue' : 'border-line bg-white text-muted',
            )}
          >
            {option}
          </button>
        ))}
      </div>

      <textarea
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        rows={2}
        placeholder={
          mode === 'approve'
            ? 'Licence PCN/FCT/2026/0091 checked against the register; premises photo matches the address.'
            : 'The premises photo shows a different address from the one on the licence.'
        }
        className="w-full rounded-btn border border-line bg-paper p-2.5 text-[13px] text-ink outline-none focus:border-blue"
      />
      <p className="m-0 mb-3 mt-1 text-[11.5px] leading-relaxed text-muted">
        {mode === 'approve'
          ? 'In six months this note is the only record of what you checked.'
          : 'The pharmacy reads this. Give them something they can act on.'}
      </p>

      {decide.error ? (
        <p role="alert" className="mb-2 text-[12.5px] font-medium text-danger">
          {decide.error instanceof ApiError ? decide.error.message : 'That did not go through.'}
        </p>
      ) : null}

      <Button
        variant={mode === 'approve' ? 'ok' : 'bad'}
        disabled={
          decide.isPending ||
          reason.trim().length < 10 ||
          (mode === 'approve' && !canApprove)
        }
        onClick={() => decide.mutate({ id, decision: mode, reason })}
      >
        {decide.isPending
          ? 'Saving…'
          : mode === 'approve'
            ? 'Approve and go live'
            : 'Send back for changes'}
      </Button>
    </section>
  );
}

function Fact({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  tone?: 'danger';
}) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted">{label}</dt>
      <dd
        className={cn(
          'm-0 font-medium text-ink',
          mono && 'font-mono',
          tone === 'danger' && 'text-danger',
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  );
}
